import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL || "https://backend.mystore.education/api";

// دالة مساعدة للحصول على التوكن
const getAuthToken = () => {
  try {
    const storedUser = JSON.parse(localStorage.getItem("loggedUser"));

    let token = storedUser?.token;
    if (!token) {
      token = storedUser?.data?.token;
    }
    if (!token) {
      token = storedUser?.user?.token;
    }
    if (!token) {
      token = storedUser?.result?.token;
    }

    return token;
  } catch (error) {
    console.error("❌ Error getting token:", error);
    return null;
  }
};

// Fetch Slider Texts
export const fetchSliderTexts = createAsyncThunk(
  "sliderTexts/fetchSliderTexts",
  async (page = 1, thunkAPI) => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get(
        `${BASE_URL}/dashboard/slider-texts?page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": "ar",
          },
        }
      );

      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "فشل في تحميل نصوص السلايدر";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Add Slider Text
export const addSliderText = createAsyncThunk(
  "sliderTexts/addSliderText",
  async (sliderTextData, thunkAPI) => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.post(
        `${BASE_URL}/dashboard/slider-texts`,
        {
          text_en: sliderTextData.text_en,
          text_ar: sliderTextData.text_ar,
          status: sliderTextData.status
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": "ar",
          },
        }
      );

      return {
        message: response.data?.message || "Slider text added successfully",
        data: response.data?.data || response.data,
        success: true
      };
    } catch (error) {
      const message = error.response?.data?.message || "فشل في إضافة نص السلايدر";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Update Slider Text
export const updateSliderText = createAsyncThunk(
  "sliderTexts/updateSliderText",
  async ({ id, sliderTextData }, thunkAPI) => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const formData = new FormData();
      formData.append("text_en", sliderTextData.text_en);
      formData.append("text_ar", sliderTextData.text_ar);
      formData.append("status", sliderTextData.status);

      const response = await axios.post(
        `${BASE_URL}/dashboard/slider-texts/${id}/update`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
            "Accept-Language": "ar",
          },
        }
      );

      return {
        message: response.data?.message || "Slider text updated successfully",
        data: response.data?.data || response.data,
        success: true
      };
    } catch (error) {
      const message = error.response?.data?.message || "فشل في تحديث نص السلايدر";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Delete Slider Text
export const deleteSliderText = createAsyncThunk(
  "sliderTexts/deleteSliderText",
  async (id, thunkAPI) => {
    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.delete(
        `${BASE_URL}/dashboard/slider-texts/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": "ar",
          },
        }
      );

      return {
        message: response.data?.message || "Slider text deleted successfully",
        id: id,
        success: true
      };
    } catch (error) {
      const message = error.response?.data?.message || "فشل في حذف نص السلايدر";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const initialState = {
  data: [],
  meta: null,
  loading: false,
  error: null,
  addLoading: false,
  addError: null,
  addSuccessMessage: null,
  updateLoading: false,
  updateError: null,
  updateSuccessMessage: null,
  deleteLoading: false,
  deleteError: null,
  deleteSuccessMessage: null,
};

const sliderTextSlice = createSlice({
  name: "sliderTexts",
  initialState,
  reducers: {
    clearSliderTextState: (state) => {
      state.error = null;
      state.addError = null;
      state.addSuccessMessage = null;
      state.updateError = null;
      state.updateSuccessMessage = null;
      state.deleteError = null;
      state.deleteSuccessMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Slider Texts
      .addCase(fetchSliderTexts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSliderTexts.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data || action.payload;
        state.meta = action.payload.meta || null;
        state.error = null;
      })
      .addCase(fetchSliderTexts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add Slider Text
      .addCase(addSliderText.pending, (state) => {
        state.addLoading = true;
        state.addError = null;
        state.addSuccessMessage = null;
      })
      .addCase(addSliderText.fulfilled, (state, action) => {
        state.addLoading = false;
        state.addSuccessMessage = action.payload.message;
        state.addError = null;
        if (action.payload.data) {
          state.data.push(action.payload.data);
        }
      })
      .addCase(addSliderText.rejected, (state, action) => {
        state.addLoading = false;
        state.addError = action.payload;
        state.addSuccessMessage = null;
      })

      // Update Slider Text
      .addCase(updateSliderText.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
        state.updateSuccessMessage = null;
      })
      .addCase(updateSliderText.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccessMessage = action.payload.message;
        state.updateError = null;
        if (action.payload.data) {
          const index = state.data.findIndex(sliderText => sliderText.id === action.payload.data.id);
          if (index !== -1) {
            state.data[index] = action.payload.data;
          }
        }
      })
      .addCase(updateSliderText.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
        state.updateSuccessMessage = null;
      })

      // Delete Slider Text
      .addCase(deleteSliderText.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
        state.deleteSuccessMessage = null;
      })
      .addCase(deleteSliderText.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deleteSuccessMessage = action.payload.message;
        state.deleteError = null;
        state.data = state.data.filter(sliderText => sliderText.id !== action.payload.id);
      })
      .addCase(deleteSliderText.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload;
        state.deleteSuccessMessage = null;
      });
  },
});

export const { clearSliderTextState } = sliderTextSlice.actions;
export default sliderTextSlice.reducer; 